import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, AlertTriangle, DollarSign, Users, Activity, Target,
  Zap, Brain, Bell, Download, RefreshCw, Filter, ChevronRight,
  TrendingDown, BarChart3, PieChart, Lock
} from 'lucide-react';
import { analyticsAPI } from '../services/api';
import toast from 'react-hot-toast';

// ─── Mobile detection ─────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtUSD = (n) => '$' + (n != null ? Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00');
const fmtNum = (n) => n != null ? Number(n).toLocaleString() : '0';

// ─── Section Card ─────────────────────────────────────────────────────────────
function SectionCard({ title, icon: Icon, color, children, action }) {
  return (
    <div className="rounded-xl border overflow-hidden"
      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
      <div className="px-4 sm:px-6 py-4 border-b flex items-center justify-between"
        style={{ borderColor: 'var(--border-color)' }}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)` }}>
            <Icon className="w-4 h-4" style={{ color }} />
          </div>
          <h3 className="text-sm sm:text-base font-bold" style={{ color: 'var(--text-primary)' }}>
            {title}
          </h3>
        </div>
        {action}
      </div>
      <div className="p-4 sm:p-6">
        {children}
      </div>
    </div>
  );
}

// ─── Metric Card ──────────────────────────────────────────────────────────────
function MetricCard({ label, value, change, icon: Icon, color, trend }) {
  return (
    <div className="p-4 rounded-xl border"
      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>
          {label}
        </p>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <p className="text-xl sm:text-2xl font-bold font-mono mb-1" style={{ color }}>
        {value}
      </p>
      {change && (
        <div className="flex items-center gap-1">
          {trend === 'up' ? (
            <TrendingUp className="w-3 h-3" style={{ color: 'var(--accent-success)' }} />
          ) : trend === 'down' ? (
            <TrendingDown className="w-3 h-3" style={{ color: 'var(--accent-danger)' }} />
          ) : null}
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{change}</p>
        </div>
      )}
    </div>
  );
}

// ─── Risk Heatmap Grid ────────────────────────────────────────────────────────
function RiskHeatmapGrid({ accounts }) {
  if (!accounts || accounts.length === 0) {
    return <p className="text-sm text-center py-8" style={{ color: 'var(--text-secondary)' }}>No accounts to display</p>;
  }

  const getRiskColor = (level) => {
    switch (level) {
      case 'critical': return 'var(--accent-danger)';
      case 'high': return 'var(--accent-warning)';
      case 'medium': return 'var(--accent-info)';
      case 'low': return 'var(--accent-success)';
      default: return 'var(--text-tertiary)';
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {accounts.slice(0, 12).map((account) => (
        <div key={account.id} className="p-3 rounded-lg border hover:shadow-lg transition-all cursor-pointer"
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            borderColor: getRiskColor(account.risk_level),
            borderWidth: '2px'
          }}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>
                {account.first_name} {account.last_name}
              </p>
              <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                #{account.login}
              </p>
            </div>
            <div className="text-right">
              <p className="text-lg font-black font-mono" style={{ color: getRiskColor(account.risk_level) }}>
                {account.risk_score}
              </p>
              <p className="text-[10px] uppercase font-bold" style={{ color: getRiskColor(account.risk_level) }}>
                {account.risk_level}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <p style={{ color: 'var(--text-tertiary)' }}>Equity</p>
              <p className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                {fmtUSD(account.equity)}
              </p>
            </div>
            <div>
              <p style={{ color: 'var(--text-tertiary)' }}>Margin Level</p>
              <p className="font-mono font-semibold" style={{ color: getRiskColor(account.risk_level) }}>
                {parseFloat(account.margin_level).toFixed(0)}%
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── CLV Segments Chart ───────────────────────────────────────────────────────
function CLVSegmentsChart({ summary }) {
  const segments = [
    { label: 'High Value', value: summary?.high_value_count || 0, color: 'var(--accent-success)' },
    { label: 'Medium Value', value: summary?.medium_value_count || 0, color: 'var(--accent-info)' },
    { label: 'Low Value', value: summary?.low_value_count || 0, color: 'var(--text-tertiary)' },
  ];

  const total = segments.reduce((sum, s) => sum + s.value, 0);
  
  return (
    <div className="space-y-3">
      {segments.map((segment, idx) => {
        const percentage = total > 0 ? (segment.value / total * 100).toFixed(1) : 0;
        return (
          <div key={idx}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                {segment.label}
              </span>
              <span className="text-xs font-mono" style={{ color: 'var(--text-tertiary)' }}>
                {segment.value} ({percentage}%)
              </span>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--border-color)' }}>
              <div className="h-full rounded-full transition-all duration-500"
                style={{ width: `${percentage}%`, backgroundColor: segment.color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Symbol Exposure Table ────────────────────────────────────────────────────
function SymbolExposureTable({ symbols }) {
  if (!symbols || symbols.length === 0) {
    return <p className="text-sm text-center py-8" style={{ color: 'var(--text-secondary)' }}>No symbol data available</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b text-left" style={{ borderColor: 'var(--border-color)' }}>
            <th className="pb-3 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>Symbol</th>
            <th className="pb-3 text-xs font-semibold uppercase tracking-wide text-right" style={{ color: 'var(--text-secondary)' }}>Exposure</th>
            <th className="pb-3 text-xs font-semibold uppercase tracking-wide text-right" style={{ color: 'var(--text-secondary)' }}>Positions</th>
            <th className="pb-3 text-xs font-semibold uppercase tracking-wide text-right" style={{ color: 'var(--text-secondary)' }}>P&L</th>
            <th className="pb-3 text-xs font-semibold uppercase tracking-wide text-right" style={{ color: 'var(--text-secondary)' }}>Win Rate</th>
          </tr>
        </thead>
        <tbody>
          {symbols.slice(0, 10).map((symbol, idx) => (
            <tr key={idx} className="border-b hover:bg-[var(--bg-tertiary)] transition-smooth"
              style={{ borderColor: 'var(--border-color)' }}>
              <td className="py-3 text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                {symbol.symbol}
              </td>
              <td className="py-3 text-sm font-mono text-right" style={{ color: 'var(--text-primary)' }}>
                {fmtUSD(symbol.total_exposure)}
              </td>
              <td className="py-3 text-sm font-mono text-right" style={{ color: 'var(--text-primary)' }}>
                {fmtNum(symbol.total_positions)}
              </td>
              <td className="py-3 text-sm font-mono text-right"
                style={{ color: parseFloat(symbol.total_pnl) >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)' }}>
                {fmtUSD(symbol.total_pnl)}
              </td>
              <td className="py-3 text-sm font-mono text-right" style={{ color: 'var(--text-primary)' }}>
                {parseFloat(symbol.win_rate || 0).toFixed(1)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Trading Patterns List ────────────────────────────────────────────────────
function TradingPatternsList({ patterns }) {
  if (!patterns || patterns.length === 0) {
    return <p className="text-sm text-center py-8" style={{ color: 'var(--text-secondary)' }}>No patterns detected</p>;
  }

  const getPatternColor = (type) => {
    switch (type) {
      case 'scalper': return 'var(--accent-warning)';
      case 'hft': return 'var(--accent-danger)';
      case 'day_trader': return 'var(--accent-info)';
      default: return 'var(--text-primary)';
    }
  };

  const getPatternLabel = (type) => {
    switch (type) {
      case 'scalper': return 'Scalper';
      case 'hft': return 'High Frequency';
      case 'day_trader': return 'Day Trader';
      case 'swing_trader': return 'Swing Trader';
      case 'copy_trader': return 'Copy Trader';
      case 'ea_user': return 'EA User';
      default: return type;
    }
  };

  return (
    <div className="space-y-2">
      {patterns.slice(0, 8).map((pattern, idx) => (
        <div key={idx} className="flex items-center justify-between p-3 rounded-lg"
          style={{ backgroundColor: 'var(--bg-tertiary)' }}>
          <div className="flex-1">
            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              {pattern.email}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase"
                style={{
                  backgroundColor: `color-mix(in srgb, ${getPatternColor(pattern.pattern_type)} 15%, transparent)`,
                  color: getPatternColor(pattern.pattern_type)
                }}>
                {getPatternLabel(pattern.pattern_type)}
              </span>
              <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                {pattern.trade_count} trades
              </span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs font-mono" style={{ color: 'var(--text-tertiary)' }}>
              Avg Hold: {(pattern.avg_hold_seconds / 60).toFixed(1)}m
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── AI Insights Panel ────────────────────────────────────────────────────────
function AIInsightsPanel({ insights }) {
  if (!insights || insights.length === 0) {
    return (
      <div className="text-center py-8">
        <Brain className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-tertiary)' }} />
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No insights available yet</p>
      </div>
    );
  }

  const getInsightIcon = (type) => {
    switch (type) {
      case 'risk': return AlertTriangle;
      case 'opportunity': return Zap;
      case 'warning': return AlertTriangle;
      default: return Brain;
    }
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'risk': return 'var(--accent-danger)';
      case 'opportunity': return 'var(--accent-success)';
      case 'warning': return 'var(--accent-warning)';
      default: return 'var(--accent-info)';
    }
  };

  return (
    <div className="space-y-3">
      {insights.map((insight, idx) => {
        const Icon = getInsightIcon(insight.type);
        const color = getInsightColor(insight.type);
        
        return (
          <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border"
            style={{
              backgroundColor: `color-mix(in srgb, ${color} 8%, transparent)`,
              borderColor: `color-mix(in srgb, ${color} 25%, transparent)`
            }}>
            <Icon className="w-5 h-5 shrink-0 mt-0.5" style={{ color }} />
            <div className="flex-1">
              <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                {insight.title}
              </p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                {insight.message}
              </p>
              {insight.action && (
                <button className="mt-2 text-xs font-semibold hover:underline"
                  style={{ color }}>
                  {insight.action} →
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function AdvancedAnalyticsPage() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const response = await analyticsAPI.getDashboard();
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load advanced analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboard();
    setRefreshing(false);
    toast.success('Analytics refreshed');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Loading advanced analytics...</p>
        </div>
      </div>
    );
  }

  const { riskIntelligence, clientLifetimeValue, symbolIntelligence, tradingPatterns, revenueOptimization } = dashboardData || {};

  // Mock insights for demo (replace with real AI insights from backend)
  const mockInsights = [
    {
      type: 'risk',
      title: 'High Risk Concentration in EURUSD',
      message: 'Your total exposure to EURUSD has increased by 45% this week. Consider reviewing position limits.',
      action: 'View Details'
    },
    {
      type: 'opportunity',
      title: 'Client Retention Opportunity',
      message: '15 high-value clients haven\'t traded in 7 days. Automated re-engagement could recover $12,000 in monthly revenue.',
      action: 'Start Campaign'
    },
    {
      type: 'warning',
      title: 'Liquidation Risk Alert',
      message: '12 accounts predicted to hit margin call within next 6 hours based on current market volatility.',
      action: 'View Accounts'
    }
  ];

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE LAYOUT
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div className="flex flex-col -mx-4 -my-6 min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
        
        {/* Hero Header */}
        <div className="px-4 pt-5 pb-6" style={{
          background: 'linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 60%, var(--accent-info)) 100%)'
        }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-white/70 text-xs font-semibold uppercase tracking-widest mb-1">
                Advanced Analytics
              </p>
              <h1 className="text-2xl font-black text-white">Premium Insights</h1>
            </div>
            <button onClick={handleRefresh} disabled={refreshing}
              className="p-2 rounded-lg bg-white/20 text-white">
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="px-4 pt-4 space-y-4 pb-8">

          {/* AI Insights */}
          <SectionCard
            title="AI Insights"
            icon={Brain}
            color="var(--accent-primary)">
            <AIInsightsPanel insights={mockInsights} />
          </SectionCard>

          {/* Risk Intelligence */}
          <SectionCard
            title="Risk Heatmap"
            icon={AlertTriangle}
            color="var(--accent-danger)"
            action={
              <button className="text-xs font-semibold" style={{ color: 'var(--accent-primary)' }}>
                View All
              </button>
            }>
            <RiskHeatmapGrid accounts={riskIntelligence?.heatmap?.accounts || []} />
          </SectionCard>

          {/* CLV Analytics */}
          <SectionCard
            title="Client Lifetime Value"
            icon={DollarSign}
            color="var(--accent-success)">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Total LTV</p>
                <p className="text-lg font-bold font-mono" style={{ color: 'var(--accent-success)' }}>
                  {fmtUSD(clientLifetimeValue?.summary?.total_ltv)}
                </p>
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Avg LTV</p>
                <p className="text-lg font-bold font-mono" style={{ color: 'var(--accent-info)' }}>
                  {fmtUSD(clientLifetimeValue?.summary?.avg_ltv)}
                </p>
              </div>
            </div>
            <CLVSegmentsChart summary={clientLifetimeValue?.summary} />
          </SectionCard>

          {/* Symbol Intelligence */}
          <SectionCard
            title="Top Symbols"
            icon={BarChart3}
            color="var(--accent-info)">
            <SymbolExposureTable symbols={symbolIntelligence} />
          </SectionCard>

          {/* Trading Patterns */}
          <SectionCard
            title="Detected Patterns"
            icon={Activity}
            color="var(--accent-warning)">
            <TradingPatternsList patterns={tradingPatterns} />
          </SectionCard>

        </div>

        <div className="pb-[env(safe-area-inset-bottom)]" />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP LAYOUT
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            Advanced Analytics
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            AI-powered insights and predictive intelligence
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleRefresh} disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-semibold transition-all hover:bg-[var(--bg-secondary)]"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ backgroundColor: 'var(--accent-primary)' }}>
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          label="Total Client LTV"
          value={fmtUSD(clientLifetimeValue?.summary?.total_ltv)}
          change="+12.5% vs last month"
          icon={DollarSign}
          color="var(--accent-success)"
          trend="up"
        />
        <MetricCard
          label="High Risk Accounts"
          value={riskIntelligence?.heatmap?.summary?.critical || 0}
          change={`${riskIntelligence?.heatmap?.summary?.high || 0} approaching`}
          icon={AlertTriangle}
          color="var(--accent-danger)"
          trend="down"
        />
        <MetricCard
          label="Detected Patterns"
          value={tradingPatterns?.length || 0}
          change="Scalpers & HFT traders"
          icon={Activity}
          color="var(--accent-warning)"
        />
        <MetricCard
          label="Churn Risk"
          value={`${clientLifetimeValue?.summary?.high_churn_risk || 0} clients`}
          change={`${parseFloat(clientLifetimeValue?.summary?.avg_churn_risk || 0).toFixed(1)}% avg risk`}
          icon={Users}
          color="var(--accent-info)"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* AI Insights */}
          <SectionCard
            title="AI-Powered Insights"
            icon={Brain}
            color="var(--accent-primary)"
            action={
              <button className="text-xs font-semibold flex items-center gap-1"
                style={{ color: 'var(--accent-primary)' }}>
                View All <ChevronRight className="w-3 h-3" />
              </button>
            }>
            <AIInsightsPanel insights={mockInsights} />
          </SectionCard>

          {/* Risk Heatmap */}
          <SectionCard
            title="Risk Intelligence Heatmap"
            icon={AlertTriangle}
            color="var(--accent-danger)"
            action={
              <button className="text-xs font-semibold flex items-center gap-1"
                style={{ color: 'var(--accent-primary)' }}>
                View All Accounts <ChevronRight className="w-3 h-3" />
              </button>
            }>
            <RiskHeatmapGrid accounts={riskIntelligence?.heatmap?.accounts || []} />
          </SectionCard>

          {/* Symbol Intelligence */}
          <SectionCard
            title="Symbol Exposure & Performance"
            icon={BarChart3}
            color="var(--accent-info)">
            <SymbolExposureTable symbols={symbolIntelligence} />
          </SectionCard>

        </div>

        {/* Right Column - 1/3 width */}
        <div className="space-y-6">
          
          {/* CLV Summary */}
          <SectionCard
            title="Client Lifetime Value"
            icon={DollarSign}
            color="var(--accent-success)">
            <div className="space-y-4">
              <div>
                <p className="text-xs mb-1" style={{ color: 'var(--text-tertiary)' }}>Total LTV</p>
                <p className="text-2xl font-bold font-mono" style={{ color: 'var(--accent-success)' }}>
                  {fmtUSD(clientLifetimeValue?.summary?.total_ltv)}
                </p>
              </div>
              <div>
                <p className="text-xs mb-1" style={{ color: 'var(--text-tertiary)' }}>Average LTV</p>
                <p className="text-xl font-bold font-mono" style={{ color: 'var(--accent-info)' }}>
                  {fmtUSD(clientLifetimeValue?.summary?.avg_ltv)}
                </p>
              </div>
              <div className="pt-3 border-t" style={{ borderColor: 'var(--border-color)' }}>
                <p className="text-xs font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Client Segments
                </p>
                <CLVSegmentsChart summary={clientLifetimeValue?.summary} />
              </div>
            </div>
          </SectionCard>

          {/* Trading Patterns */}
          <SectionCard
            title="Trading Patterns"
            icon={Activity}
            color="var(--accent-warning)">
            <TradingPatternsList patterns={tradingPatterns} />
          </SectionCard>

        </div>
      </div>

    </div>
  );
}